// Shared http(s) browse-target rules for write-time asserts and runtime navigation guards.
//
// A URL the QA browser may open: http/https only. Private / loopback hosts require allowPrivateHost
// (reverse-proxy projects, eval mirrors served on localhost). Hostname-string checks alone are not
// enough — public DNS names (nip.io, localtest.me, …) and IPv4-mapped IPv6 also resolve to loopback.
// Full DNS resolution is async (see hostnameResolvesPrivate); sync asserts use the alias denylist +
// literal checks. Browser navigation blocking resolves DNS at request time.

import dns from 'node:dns/promises'

export type HttpTargetUrlOptions = {
  /** Reverse-proxy projects and localhost mirrors may name a private-network host. Non-http(s) schemes are still rejected. */
  allowPrivateHost?: boolean
  field?: string
}

// Public hostnames that always resolve to loopback / the caller's machine. Synced denylist for
// write-time asserts; runtime also DNS-resolves (hostnameResolvesPrivate) so custom rebinding is caught.
const LOOPBACK_ALIAS_HOST_RE = /(^|\.)(nip\.io|sslip\.io|localtest\.me|lvh\.me|vcap\.me|xip\.io)$/i

function stripHostBrackets(hostname: string): string {
  return hostname.toLowerCase().replace(/\.$/, '').replace(/^\[|\]$/g, '')
}

/** IPv4 dotted-quad → private? */
function isPrivateIpv4(host: string): boolean {
  const v4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/)
  if (!v4) return false
  const [a, b] = [Number(v4[1]), Number(v4[2])]
  if (a === 10 || a === 127) return true
  if (a === 192 && b === 168) return true
  if (a === 172 && b >= 16 && b <= 31) return true
  if (a === 169 && b === 254) return true // link-local
  if (a === 0) return true // 0.0.0.0/8
  return false
}

/** ::ffff:127.0.0.1 / ::ffff:7f00:1 → embedded IPv4, or null. */
function ipv4MappedAddress(host: string): string | null {
  const m = host.match(/^::ffff:(.+)$/i)
  if (!m) return null
  const rest = m[1]!
  if (/^\d+\.\d+\.\d+\.\d+$/.test(rest)) return rest
  const hex = rest.match(/^([0-9a-f]{1,4}):([0-9a-f]{1,4})$/i)
  if (!hex) return null
  const hi = parseInt(hex[1]!, 16)
  const lo = parseInt(hex[2]!, 16)
  return `${(hi >> 8) & 255}.${hi & 255}.${(lo >> 8) & 255}.${lo & 255}`
}

function isIpLiteral(host: string): boolean {
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return true
  if (host.includes(':')) return true
  return false
}

// Localhost / private-network / non-routable host. Covers localhost, *.local, single-label hostnames
// (no dot), loopback / link-local / RFC-1918 IPv4, IPv4-mapped IPv6, IPv6 loopback / ULA / link-local,
// and well-known loopback DNS aliases.
export function isPrivateHost(hostname: string): boolean {
  const host = stripHostBrackets(hostname)
  if (!host) return true
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local')) return true
  if (LOOPBACK_ALIAS_HOST_RE.test(host)) return true
  if (host === '::1' || host === '0.0.0.0' || host === '::') return true

  const mapped = ipv4MappedAddress(host)
  if (mapped) return isPrivateIpv4(mapped)

  if (isPrivateIpv4(host)) return true

  // IPv6 link-local fe80::/10, unique-local fc00::/7.
  if (host.includes(':')) {
    const first = host.split(':')[0] ?? ''
    const n = parseInt(first, 16)
    if (!Number.isNaN(n)) {
      if ((n & 0xffc0) === 0xfe80) return true // fe80::/10
      if ((n & 0xfe00) === 0xfc00) return true // fc00::/7
    }
    return false
  }

  // A bare single-label host (e.g. "my-dev-box") isn't publicly resolvable.
  if (!host.includes('.')) return true
  return false
}

/** True when DNS answers with any private / loopback address. Fail-open on lookup errors. */
export async function hostnameResolvesPrivate(hostname: string): Promise<boolean> {
  const host = stripHostBrackets(hostname)
  if (!host || isIpLiteral(host)) return false
  try {
    const results = await dns.lookup(host, { all: true })
    return results.some((r) => isPrivateHost(r.address))
  } catch {
    return false
  }
}

/** A URL the QA browser may be pointed at. http/https only; private hosts require allowPrivateHost. */
export function assertHttpTargetUrl(raw: string, opts: HttpTargetUrlOptions = {}): URL {
  const field = opts.field ?? 'target_url'
  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    throw new Error(`Invalid ${field}: ${raw}`)
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`${field} must be an http or https URL`)
  }
  if (!opts.allowPrivateHost && isPrivateHost(parsed.hostname)) {
    throw new Error(`${field} must be a public internet URL (got a local or private-network address). Set use_reverse_proxy to test a URL only reachable from your machine.`)
  }
  return parsed
}

/** Like assertHttpTargetUrl, but also rejects hosts whose DNS answers private / loopback. */
export async function assertHttpTargetUrlResolved(raw: string, opts: HttpTargetUrlOptions = {}): Promise<URL> {
  const parsed = assertHttpTargetUrl(raw, opts)
  if (!opts.allowPrivateHost && await hostnameResolvesPrivate(parsed.hostname)) {
    const field = opts.field ?? 'target_url'
    throw new Error(`${field} must be a public internet URL (got a local or private-network address). Set use_reverse_proxy to test a URL only reachable from your machine.`)
  }
  return parsed
}

/**
 * Wrap a NavigationGuard so the URL it resolves is an allowed HttpTarget.
 * Returns null when the inner guard refuses or the resolved URL fails assertHttpTargetUrl.
 * Literal / alias checks only — DNS rebinding is blocked at the browser route (installHttpTargetNavigationBlock).
 */
export function withHttpTargetNavigationGuard(
  inner: (url: string) => string | null,
  opts: HttpTargetUrlOptions = {},
): (url: string) => string | null {
  const field = opts.field ?? 'navigate'
  return (url) => {
    const next = inner(url)
    if (next == null || next === '') return null
    try {
      assertHttpTargetUrl(next, { allowPrivateHost: opts.allowPrivateHost, field })
      return next
    } catch {
      return null
    }
  }
}
